import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Moon, 
  Trophy,
  AlertCircle,
  ArrowRight,
  Plus,
  X,
  Brain,
  Save,
  Sparkles,
  Heart
} from "lucide-react";

export default function Shutdown() {
  const [shutdowns, setShutdowns] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eveningReflection, setEveningReflection] = useState(null);
  
  const today = new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    date: today,
    wins: ["", "", ""],
    open_issues: [],
    tomorrow_priorities: ["", "", ""],
    brain_dump: "",
  });

  useEffect(() => {
    fetchData();
    fetchEveningReflection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEveningReflection = async () => {
    try {
      const res = await axios.get(`${API}/empowerment/evening`, { withCredentials: true });
      setEveningReflection(res.data);
    } catch (error) {
      console.error("Error fetching evening reflection:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [shutdownRes, tasksRes] = await Promise.all([
        axios.get(`${API}/shutdown?date=${today}`, { withCredentials: true }),
        axios.get(`${API}/tasks?priority=Today`, { withCredentials: true }),
      ]);
      
      setShutdowns(shutdownRes.data || []);
      setTodayTasks(tasksRes.data || []);
      
      // Pre-fill open issues from incomplete Today tasks
      const incompleteTasks = (tasksRes.data || [])
        .filter(t => !t.completed)
        .map(t => t.description);
      
      // If we have today's shutdown, load it
      const todayShutdown = (shutdownRes.data || []).find(s => s.date === today);
      if (todayShutdown) {
        setFormData({
          date: today,
          wins: todayShutdown.wins.length >= 3 ? todayShutdown.wins : [...todayShutdown.wins, ...Array(3 - todayShutdown.wins.length).fill("")],
          open_issues: todayShutdown.open_issues,
          tomorrow_priorities: todayShutdown.tomorrow_priorities.length >= 3 
            ? todayShutdown.tomorrow_priorities 
            : [...todayShutdown.tomorrow_priorities, ...Array(3 - todayShutdown.tomorrow_priorities.length).fill("")],
          brain_dump: todayShutdown.brain_dump || "",
        });
      } else {
        setFormData(prev => ({
          ...prev,
          open_issues: incompleteTasks,
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveShutdown = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      wins: formData.wins.filter(w => w.trim()),
      tomorrow_priorities: formData.tomorrow_priorities.filter(p => p.trim()),
    };
    
    try {
      await axios.post(`${API}/shutdown`, payload, { withCredentials: true });
      toast.success("Shutdown ritual saved!", {
        description: "Great job closing out the day!"
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to save shutdown");
    }
  };

  const updateWin = (index, value) => {
    const newWins = [...formData.wins];
    newWins[index] = value;
    setFormData({ ...formData, wins: newWins });
  };

  const updatePriority = (index, value) => {
    const newPriorities = [...formData.tomorrow_priorities];
    newPriorities[index] = value;
    setFormData({ ...formData, tomorrow_priorities: newPriorities });
  };

  const addOpenIssue = () => {
    setFormData({ ...formData, open_issues: [...formData.open_issues, ""] });
  };

  const updateOpenIssue = (index, value) => {
    const newIssues = [...formData.open_issues];
    newIssues[index] = value;
    setFormData({ ...formData, open_issues: newIssues });
  };

  const removeOpenIssue = (index) => {
    const newIssues = formData.open_issues.filter((_, i) => i !== index);
    setFormData({ ...formData, open_issues: newIssues });
  };

  const completedTasks = todayTasks.filter(t => t.completed);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="shutdown-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Moon className="w-6 h-6 text-indigo-500" />
            Daily Shutdown Ritual
          </h1>
          <p className="text-muted-foreground">Close the day, clear the mind</p>
        </div>
        
        <Badge variant="outline" className="text-sm">
          {today}
        </Badge>
      </div>

      {/* Evening Reflection Banner */}
      {eveningReflection && (
        <Card className="bento-card border-indigo-500/30 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="text-lg italic text-foreground/90">
                  &ldquo;{eveningReflection.quote?.quote}&rdquo;
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  — {eveningReflection.quote?.author}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm text-indigo-300">{eveningReflection.closing_message}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={saveShutdown} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 3 Wins */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-emerald-400" />
                </div>
                3 Wins
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">What went well today?</p>
              {formData.wins.map((win, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs text-emerald-400 font-bold">
                    {index + 1}
                  </div>
                  <Input
                    value={win}
                    onChange={(e) => updateWin(index, e.target.value)}
                    placeholder={`Win #${index + 1}`}
                    data-testid={`win-${index}`}
                  />
                </div>
              ))}
              
              {/* Show completed tasks as suggestions */}
              {completedTasks.length > 0 && (
                <div className="pt-2 border-t border-zinc-800">
                  <p className="text-xs text-muted-foreground mb-2">Completed today:</p>
                  <div className="flex flex-wrap gap-1">
                    {completedTasks.slice(0, 5).map((task) => (
                      <Badge 
                        key={task.task_id}
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-emerald-500/20"
                        onClick={() => {
                          const emptyIndex = formData.wins.findIndex(w => !w);
                          if (emptyIndex !== -1) {
                            updateWin(emptyIndex, task.description);
                          }
                        }}
                      >
                        {task.description}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 3 Open Issues */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  </div>
                  Open Issues
                </div>
                <Button 
                  type="button"
                  variant="ghost" 
                  size="sm"
                  onClick={addOpenIssue}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">What&apos;s still on your mind?</p>
              {formData.open_issues.map((issue, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <AlertCircle className="w-3 h-3 text-amber-400" />
                  </div>
                  <Input
                    value={issue}
                    onChange={(e) => updateOpenIssue(index, e.target.value)}
                    placeholder="Open issue..."
                    className="flex-1"
                    data-testid={`issue-${index}`}
                  />
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeOpenIssue(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {formData.open_issues.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No open issues - great!
                </p>
              )}
            </CardContent>
          </Card>

          {/* 3 Tomorrow Priorities */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-blue-400" />
                </div>
                3 Tomorrow Priorities
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">What must happen tomorrow?</p>
              {formData.tomorrow_priorities.map((priority, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs text-blue-400 font-bold">
                    {index + 1}
                  </div>
                  <Input
                    value={priority}
                    onChange={(e) => updatePriority(index, e.target.value)}
                    placeholder={`Priority #${index + 1}`}
                    data-testid={`priority-${index}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Brain Dump */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-violet-400" />
                </div>
                Brain Dump
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">Anything else on your mind?</p>
              <Textarea
                value={formData.brain_dump}
                onChange={(e) => setFormData({ ...formData, brain_dump: e.target.value })}
                placeholder="Free-form notes, ideas, concerns..."
                rows={6}
                data-testid="brain-dump"
              />
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" size="lg" data-testid="save-shutdown">
            <Save className="w-4 h-4 mr-2" />
            Complete Shutdown Ritual
          </Button>
        </div>
      </form>
    </div>
  );
}
